/**
 * The Brand Configuration page's form model (firm-model spec §6.2, §10.2; A8 per the Figma
 * frames): the firm record as an editable draft and back, the four sections and their
 * override switches, validation and the "Last saved" line. Pure, so the page stays wiring +
 * JSX.
 */
import { isFieldAllowed } from "@contract/standards/resolve";
import {
  STANDARDS_FIELDS,
  type AllowMask,
  type FirmStandards,
  type FirmStandardsRecord,
  type StandardsField,
} from "@contract/standards/standards";
import {
  CURRENCY_INCOMPLETE_COPY,
  currencyIncomplete,
  formatSelectsOf,
  formatsFromSelects,
  type FormatSelects,
} from "@contract/standards/selectOptions";
import { formatDate } from "./homeModel";

/**
 * The page's four cards, each with one "Allow team members to overwrite ..." switch (Dan
 * 2026-10-02: per section, not per field). The stored mask stays per field: a section's
 * switch sets every field in it. "Other label rules" is hidden on the page but still sits
 * in Labels, so the Labels switch locks it too.
 */
type StandardsSection = "colors" | "typography" | "language" | "labels";

const SECTION_FIELDS: Record<StandardsSection, readonly StandardsField[]> = {
  colors: ["brandPalette"],
  typography: ["brandFonts"],
  language: ["audiencePurpose", "bannedWords", "languageVariant"],
  labels: ["currencyFormat", "calendarYearFormat", "fiscalYearFormat", "otherLabelRules"],
};

/** The add-in's six series slots (Accent 1–6). */
const SERIES_SLOTS = 6;

/** The Brand Profile's font size range, in whole points. */
const SIZE_MIN = 6;
const SIZE_MAX = 96;

/** The form's values. The four format fields are closed selects (standards selects spec),
 * one string per select, "" = Not set. Series colors are always six slots; sizes are "" when
 * not set. */
export type StandardsDraft = FormatSelects & {
  audiencePurpose: string;
  bannedWords: string[];
  otherLabelRules: string;
  seriesColors: string[];
  positiveColor: string;
  negativeColor: string;
  highlightColor: string;
  headingFont: string;
  bodyFont: string;
  headingSize: number | "";
  bodySize: number | "";
  allow: Record<StandardsField, boolean>;
};

export function toDraft(record: Pick<FirmStandardsRecord, "standards" | "allow">): StandardsDraft {
  const s = record.standards;
  const allow = {} as Record<StandardsField, boolean>;
  for (const field of STANDARDS_FIELDS) allow[field] = isFieldAllowed(record.allow, field);
  const series = s.brand?.palette?.series ? s.brand.palette.series.slice(0, SERIES_SLOTS) : [];
  while (series.length < SERIES_SLOTS) series.push("");
  const fonts = s.brand?.fonts;
  return {
    ...formatSelectsOf(s),
    audiencePurpose: s.audiencePurpose ?? "",
    bannedWords: s.bannedWords ? s.bannedWords.slice() : [],
    otherLabelRules: s.otherLabelRules ?? "",
    seriesColors: series,
    positiveColor: s.brand?.palette?.positive ?? "",
    negativeColor: s.brand?.palette?.negative ?? "",
    highlightColor: s.brand?.palette?.highlight ?? "",
    headingFont: fonts?.heading ?? "",
    bodyFont: fonts?.body ?? "",
    headingSize: fonts?.headingSizePt ?? "",
    bodySize: fonts?.bodySizePt ?? "",
    allow,
  };
}

const trimmed = (value: string): string | undefined => (value.trim() ? value.trim() : undefined);
const color = (value: string): string | undefined => (value.trim() ? value.trim().toUpperCase() : undefined);
const sizeOk = (value: number | ""): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= SIZE_MIN && value <= SIZE_MAX;

/** The wire values and mask for a save. Blank fields are left out (no standard); the mask
 * stores only the locks, since an absent key means allowed. */
export function fromDraft(draft: StandardsDraft): { standards: FirmStandards; allow: AllowMask } {
  const standards: FirmStandards = formatsFromSelects(draft);
  const set = <K extends keyof FirmStandards>(key: K, value: FirmStandards[K] | undefined) => {
    if (value !== undefined) standards[key] = value;
  };
  set("audiencePurpose", trimmed(draft.audiencePurpose));
  if (draft.bannedWords.length > 0) standards.bannedWords = draft.bannedWords.slice();
  set("otherLabelRules", trimmed(draft.otherLabelRules));

  const palette: NonNullable<NonNullable<FirmStandards["brand"]>["palette"]> = {};
  const series = draft.seriesColors.map((c) => color(c)).filter((c): c is string => c !== undefined);
  if (series.length > 0) palette.series = series;
  if (color(draft.positiveColor)) palette.positive = color(draft.positiveColor);
  if (color(draft.negativeColor)) palette.negative = color(draft.negativeColor);
  if (color(draft.highlightColor)) palette.highlight = color(draft.highlightColor);
  const fonts: NonNullable<NonNullable<FirmStandards["brand"]>["fonts"]> = {};
  if (trimmed(draft.headingFont)) fonts.heading = trimmed(draft.headingFont);
  if (trimmed(draft.bodyFont)) fonts.body = trimmed(draft.bodyFont);
  if (sizeOk(draft.headingSize)) fonts.headingSizePt = draft.headingSize;
  if (sizeOk(draft.bodySize)) fonts.bodySizePt = draft.bodySize;
  if (Object.keys(palette).length > 0 || Object.keys(fonts).length > 0) {
    standards.brand = {};
    if (Object.keys(palette).length > 0) standards.brand.palette = palette;
    if (Object.keys(fonts).length > 0) standards.brand.fonts = fonts;
  }

  const allow: AllowMask = {};
  for (const field of STANDARDS_FIELDS) if (!draft.allow[field]) allow[field] = false;
  return { standards, allow };
}

/** A section's switch: on only while every field in it is allowed, so a mask saved field by
 * field before A8 reads as off until the section is saved whole. */
export function sectionAllowed(draft: StandardsDraft, section: StandardsSection): boolean {
  return SECTION_FIELDS[section].every((field) => draft.allow[field]);
}

/** The draft patch that sets every field in a section. */
export function withSectionAllowed(draft: StandardsDraft, section: StandardsSection, allowed: boolean): Pick<StandardsDraft, "allow"> {
  const allow = { ...draft.allow };
  for (const field of SECTION_FIELDS[section]) allow[field] = allowed;
  return { allow };
}

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** The first problem that would make the server refuse the save, as copy; null = valid. */
export function draftProblem(draft: StandardsDraft): string | null {
  if (currencyIncomplete(draft)) return CURRENCY_INCOMPLETE_COPY;
  const colors = draft.seriesColors.concat([draft.positiveColor, draft.negativeColor, draft.highlightColor]);
  for (const c of colors) {
    if (c.trim() && !HEX.test(c.trim())) return `"${c.trim()}" isn't a color. Use a hex code like #0048B4.`;
  }
  const lastFilled = draft.seriesColors.reduce((last, c, i) => (c.trim() ? i : last), -1);
  if (lastFilled > 0 && draft.seriesColors.slice(0, lastFilled).some((c) => !c.trim())) {
    return "Fill the accent colors in order, starting with Accent 1.";
  }
  for (const size of [draft.headingSize, draft.bodySize]) {
    if (size !== "" && !sizeOk(size)) return `Font sizes are whole points from ${SIZE_MIN} to ${SIZE_MAX}.`;
  }
  for (const word of draft.bannedWords) {
    if (word.length > 40) return `"${word.slice(0, 20)}..." is too long for a banned word (40 characters at most).`;
  }
  return null;
}

export function isDirty(draft: StandardsDraft, baseline: StandardsDraft): boolean {
  return JSON.stringify(fromDraft(draft)) !== JSON.stringify(fromDraft(baseline));
}

/** The header's meta line (Dan 2026-10-02): "Last saved: Sep 29, 2026 (Version 3)". */
export function lastSavedLine(record: FirmStandardsRecord): string {
  if (record.version === 0) return "Not saved yet";
  return record.updatedAt
    ? `Last saved: ${formatDate(record.updatedAt)} (Version ${record.version})`
    : `Version ${record.version}`;
}
